import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { IVolunteerActivity } from './models';
import { invalidateVolunteerActivities } from './useVolunteerActivities';
import { VOLUNTEER_ACTIVITY_MUTATION_SCOPE, volunteerActivityQueryKey } from './useVolunteerActivity';

export interface ISetVolunteerActivityShowOnHomepageVariables {
	activityId: string;
	showOnHomepage: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

export const useSetVolunteerActivityShowOnHomepageMutation = ({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (data: IVolunteerActivity, variables: ISetVolunteerActivityShowOnHomepageVariables) => void;
} = {}) => {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		scope: VOLUNTEER_ACTIVITY_MUTATION_SCOPE,
		mutationFn: ({ activityId, showOnHomepage, impersonationContext }: ISetVolunteerActivityShowOnHomepageVariables) =>
			userSession.webServiceHelper.callAsync<IVolunteerActivity>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `volunteering/activities/${activityId}/showOnHomepage`,
					queryParams: { showOnHomepage },
				}),
				'PUT'
			),
		// Returning the invalidation keeps the mutation pending until the refetch lands, so the
		// checkbox stays disabled instead of briefly re-enabling on the stale value.
		onSuccess: (data, variables) => {
			queryClient.setQueryData(
				volunteerActivityQueryKey(variables.activityId, variables.impersonationContext?.account?.id),
				data
			);
			onSuccess?.(data, variables);
			return invalidateVolunteerActivities();
		},
		onError,
	});
};
